import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { useFi } from '../../contexts/fi-context';
import { FiMessageBubble } from './fi-message-bubble';
import { FiWelcomeHero } from './fi-welcome-hero';
import { FI_PANEL_BG } from './fi-styles';

interface FiMessageAreaProps {
  className?: string;
  onLearnMore?: () => void;
  scrollable?: boolean;
}

export const FiMessageArea: React.FC<FiMessageAreaProps> = ({ scrollable = true, className, onLearnMore }) => {
  const { messages, isSending, fiSearchProgress, panelOpen } = useFi();
  const bottomRef = useRef<HTMLDivElement>(null);
  const messageContainerRefs = useRef<Array<HTMLDivElement | null>>([]);
  const lastAnchoredKeyRef = useRef<string | null>(null);

  const anchorIndex = useMemo(() => {
    if (!messages.length) return null;

    let lastUserIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === 'user') {
        lastUserIndex = i;
        break;
      }
    }
    if (lastUserIndex === -1) return null;

    for (let i = lastUserIndex + 1; i < messages.length; i++) {
      const m = messages[i];
      if (m?.role === 'assistant' && m.fi_kind !== 'crisis' && Array.isArray(m.tool_results) && m.tool_results.length) {
        return i;
      }
    }
    return null;
  }, [messages]);

  useLayoutEffect(() => {
    if (!panelOpen) return;

    if (anchorIndex != null) {
      const m = messages[anchorIndex];
      const key = `${anchorIndex}-${m?.timestamp ?? ''}`;
      if (lastAnchoredKeyRef.current !== key) {
        lastAnchoredKeyRef.current = key;
        messageContainerRefs.current[anchorIndex]?.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
      return;
    }

    bottomRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
  }, [messages, isSending, anchorIndex, panelOpen]);

  const showStageBelowList = isSending && anchorIndex == null;

  return (
    <div
      className={['min-h-0 flex-1', scrollable ? 'overflow-y-auto' : '', className ?? ''].filter(Boolean).join(' ')}
      style={{ backgroundColor: FI_PANEL_BG }}
    >
      <FiWelcomeHero onLearnMore={onLearnMore} />
      {messages.length > 0 || showStageBelowList ? (
        <div className="mt-4 flex flex-col gap-4 px-0.5">
          {messages.map((m, i) => (
            <div
              key={`${i}-${m.timestamp ?? ''}`}
              ref={(el) => (messageContainerRefs.current[i] = el)}
              className="scroll-mt-6"
            >
              <FiMessageBubble message={m} index={i} />
            </div>
          ))}
          {showStageBelowList ? (
            <div className="font-open-sans text-sm text-gray-500" aria-live="polite">
              <div className="flex justify-start pl-4">
                <div className="max-w-[85%] rounded-2xl bg-gray-50 px-4 py-2">
                  <p className="italic leading-5">{fiSearchProgress?.message ?? 'Fi is thinking…'}</p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      <div ref={bottomRef} />
    </div>
  );
};
